import * as Sentry from '@sentry/browser';
import { initialState } from '@/Helpers.js';
import BaseInterceptor from '@/router/interceptors/BaseInterceptor.js';
import SnackbarService from '@/services/SnackbarService.js';

// Instead of importing directly, get SessionMonitor when needed
const getSessionMonitor = () => window.SessionMonitor;

class UnauthenticatedInterceptor extends BaseInterceptor {
    async handle(status) {
        // Use paths instead of route names
        const publicPaths = ['/', '/login', '/password/email', '/password/reset'];
        const currentPath = this.$router.currentRoute.value.path;

        // Check if the path starts with any of the public paths
        const routeIncluded = publicPaths.some(
            path => currentPath === path || (path !== '/' && currentPath.startsWith(`${path}/`))
        );

        // Get SessionMonitor instance
        const sessionMonitor = getSessionMonitor();

        // If we're already on a public route, simply reset the session state and don't redirect
        if (routeIncluded) {
            // Reset state when user is already on login/public page
            if (sessionMonitor) {
                sessionMonitor.reset();
            }

            return true;
        }

        // Check if this is a 401 error - only handle 401, not 403
        // 401 = Unauthorized (not authenticated), 403 = Forbidden (authenticated but no permission)
        const isAuthError = status === 401;
        if (!isAuthError) {
            return false;
        }

        // Check if this is an expected authentication check error
        // This represents the case where the user has never been authenticated in this session
        // (e.g., initial load checks). NOT expected: session expired after being authenticated.
        // Note: Public routes are already handled by the early return above.
        const wasAuthenticated = this.$store.getters['Auth/check'];
        const isExpectedAuthError = !wasAuthenticated;

        if (isExpectedAuthError) {
            return true;
        }

        // Save the current path for return after re-authentication
        if (currentPath && !publicPaths.includes(currentPath)) {
            this.$store.dispatch('Auth/saveReturnPath', currentPath);
        }

        // If we have a SessionMonitor, use it to track the session state
        if (sessionMonitor) {
            // Call handleSessionTimeout() directly and synchronously.
            // This sets the internal flags immediately, preventing subsequent 401s
            // from triggering duplicate notifications (fixes race condition).
            // The method has its own guard to prevent duplicate handling.
            try {
                sessionMonitor.handleSessionTimeout();
            } catch (error) {
                console.error('SessionMonitor.handleSessionTimeout() threw an error:', error);

                // Report to Sentry for monitoring
                Sentry.captureException(error, {
                    tags: {
                        error_type: 'session_timeout_handler_failed',
                        component: 'UnauthenticatedInterceptor',
                    },
                    contexts: {
                        session: {
                            current_path: currentPath,
                            session_monitor_available: !!sessionMonitor,
                        },
                    },
                });
            }

            // Always update Vuex state for components that watch it, even if handleSessionTimeout() threw.
            // Note: SET_SESSION_TIMED_OUT mutation will also call handleSessionTimeout() (Auth.js:204),
            // but this is intentional - the SessionMonitor's guard prevents duplicate execution.
            // We call it here first to set flags synchronously, then dispatch to Vuex for state consistency.
            this.$store.dispatch('Auth/setSessionTimedOut', true);

            return true; // Error was handled
        } else {
            // Fallback if SessionMonitor isn't available
            this.$store.dispatch('Auth/setInitialState', {
                ...initialState,
                loggedOut: true,
            });

            // Show a warning instead of immediately redirecting
            try {
                SnackbarService.open({
                    message: 'Your session has expired. Please log in again to continue.',
                    type: 'is-warning',
                    position: 'is-top',
                    actionText: 'Log in now',
                    indefinite: true,
                    queue: false,
                    onAction: () => {
                        const redirectUrl = window.location.pathname + window.location.search + window.location.hash;
                        const loginUrl = `/login?redirect=${encodeURIComponent(redirectUrl)}`;
                        window.location.href = loginUrl;
                    },
                });
            } catch (_error) {
                // Still delay a bit to give user a chance to see what's happening
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000); // 2 seconds
            }

            return true;
        }
    }
}

export default UnauthenticatedInterceptor;
